import React, { useState, useCallback, useRef, useEffect } from "react";
import { View, Pressable, TextInput, Platform } from "react-native";
import {
  MoonFilterChip,
  MoonSegmentedControl,
  TextActionButton,
} from "@louise/moon-ui";
import { Text } from "../Text/Text";
import { Plus, X, Check } from "lucide-react-native";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { semanticColors, typography } from "@src/packages/theme/tokens";

// ============================================================================
// Types
// ============================================================================

/**
 * Preset timing values that match Profile settings
 */
export type PresetTiming = "exact" | "15m_before" | "30m_before" | "1h_before";

/**
 * Custom timing format: custom_<number><unit>
 * Examples: "custom_48m", "custom_2h", "custom_1d"
 */
export type CustomTiming = `custom_${number}${"m" | "h" | "d"}`;

/**
 * Any valid timing value (preset or custom)
 */
export type ReminderTimingValue = PresetTiming | CustomTiming;

/**
 * Helper to check if a timing is custom
 */
export function isCustomTiming(
  timing: ReminderTimingValue,
): timing is CustomTiming {
  return timing.startsWith("custom_");
}

/**
 * Parse a custom timing value to get the number and unit
 */
export function parseCustomTiming(timing: CustomTiming): {
  value: number;
  unit: "m" | "h" | "d";
} {
  const match = timing.match(/^custom_(\d+)([mhd])$/);
  if (!match) {
    return { value: 0, unit: "m" };
  }
  return {
    value: parseInt(match[1]!, 10),
    unit: match[2] as "m" | "h" | "d",
  };
}

/**
 * Create a custom timing string
 */
export function createCustomTiming(
  value: number,
  unit: "m" | "h" | "d",
): CustomTiming {
  return `custom_${value}${unit}` as CustomTiming;
}

/**
 * Format a timing value for display
 */
export function formatTimingLabel(timing: ReminderTimingValue): string {
  if (isCustomTiming(timing)) {
    const { value, unit } = parseCustomTiming(timing);
    const unitLabels: Record<"m" | "h" | "d", string> = {
      m: "min",
      h: "hr",
      d: "day",
    };
    const unitLabel = unitLabels[unit];
    // Pluralize for > 1
    const pluralSuffix = value > 1 && unit !== "m" ? "s" : "";
    return `${value} ${unitLabel}${pluralSuffix} before`;
  }

  // Preset labels
  const presetLabels: Record<PresetTiming, string> = {
    exact: "Exact time",
    "15m_before": "15m before",
    "30m_before": "30m before",
    "1h_before": "1h before",
  };
  return presetLabels[timing];
}

// ============================================================================
// Preset options
// ============================================================================

export const PRESET_TIMING_OPTIONS: { id: PresetTiming; label: string }[] = [
  { id: "exact", label: "Exact time" },
  { id: "15m_before", label: "15m before" },
  { id: "30m_before", label: "30m before" },
  { id: "1h_before", label: "1h before" },
];

// ============================================================================
// Props
// ============================================================================

/**
 * Theme colors for the optional custom-time input and muted controls.
 */
export interface ReminderTimingTheme {
  /** Unselected chip text color */
  mutedText: string;
  /** Custom input background */
  inputBg: string;
  /** Custom input border */
  inputBorder: string;
  /** Custom input text color */
  inputText: string;
  /** Cancel button background */
  cancelBg: string;
  /** Cancel button icon color */
  cancelIcon: string;
  /** Error text color */
  errorText: string;
}

const DARK_THEME: ReminderTimingTheme = {
  mutedText: "#94a3b8",
  inputBg: "#1E293B",
  inputBorder: "rgba(255,255,255,0.2)",
  inputText: "#ffffff",
  cancelBg: "rgba(255,255,255,0.1)",
  cancelIcon: "#94a3b8",
  errorText: "#f87171",
};

const LIGHT_THEME: ReminderTimingTheme = {
  mutedText: semanticColors.text.secondary,
  inputBg: semanticColors.background.app,
  inputBorder: "#E8E0D8",
  inputText: semanticColors.text.primary,
  cancelBg: "#E8E0D8",
  cancelIcon: semanticColors.text.secondary,
  errorText: semanticColors.status.danger,
};

export const TIMING_THEMES = { dark: DARK_THEME, light: LIGHT_THEME } as const;

interface ReminderTimingEditorProps {
  /**
   * Array of selected timing values
   * Can include both presets and custom values
   */
  timings: ReminderTimingValue[];
  /**
   * Called when timings change
   */
  onTimingsChange: (timings: ReminderTimingValue[]) => void;
  /**
   * Whether to allow multiple timings to be selected
   * @default true
   */
  allowMultiple?: boolean;
  /**
   * Whether people can add an arbitrary timing in addition to the presets.
   * Existing custom timings still render so previously saved values remain
   * visible and removable.
   * @default true
   */
  allowCustomTiming?: boolean;
  /**
   * Whether the final selected timing can be removed. Callers that enable
   * this must interpret an empty array as their own "no schedule" state.
   * @default false
   */
  allowEmptySelection?: boolean;
  /**
   * Whether the editor is disabled
   * @default false
   */
  disabled?: boolean;
  /**
   * Fill of the custom-time form's confirm button. The timing chips do not
   * take it: they are the shared filter chip in its standard selection colour.
   * @default "#4ADE80"
   */
  accentColor?: string;
  /**
   * Theme preset or custom theme colors for unselected states.
   * @default "dark"
   */
  theme?: "dark" | "light" | ReminderTimingTheme;
}

// ============================================================================
// Custom Time Input Component
// ============================================================================

interface CustomTimeInputProps {
  onAdd: (timing: CustomTiming) => void;
  onCancel: () => void;
  existingTimings: ReminderTimingValue[];
  accentColor: string;
  colors: ReminderTimingTheme;
  /** The unit pill tabs' tone: cream on the dark theme. */
  segmentedTone: "default" | "inverse";
}

function CustomTimeInput({
  onAdd,
  onCancel,
  existingTimings,
  accentColor,
  colors,
  segmentedTone,
}: CustomTimeInputProps) {
  const [value, setValue] = useState("");
  const capturePress = usePressCapture("CustomTimingForm");
  const [unit, setUnit] = useState<"m" | "h" | "d">("m");
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<TextInput>(null);

  // Focus input on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  const handleAdd = useCallback(() => {
    const numValue = parseInt(value, 10);

    // Validation
    if (!value.trim() || isNaN(numValue)) {
      setError("Enter a number");
      return;
    }

    if (numValue <= 0) {
      setError("Must be greater than 0");
      return;
    }

    if (numValue > 999) {
      setError("Value too large");
      return;
    }

    // Check for duplicates
    const newTiming = createCustomTiming(numValue, unit);
    if (existingTimings.includes(newTiming)) {
      setError("Already added");
      return;
    }

    // Also check if it matches a preset
    const presetEquivalents: Record<string, PresetTiming> = {
      "15m": "15m_before",
      "30m": "30m_before",
      "60m": "1h_before",
      "1h": "1h_before",
    };
    const equivalentKey = `${numValue}${unit}`;
    const preset = presetEquivalents[equivalentKey];
    if (preset && existingTimings.includes(preset)) {
      setError("Use preset option");
      return;
    }

    setError(null);
    onAdd(newTiming);
  }, [value, unit, existingTimings, onAdd]);

  const unitOptions: { id: "m" | "h" | "d"; label: string }[] = [
    { id: "m", label: "min" },
    { id: "h", label: "hr" },
    { id: "d", label: "day" },
  ];

  return (
    <View className="flex-row items-center gap-2">
      {/* Number input */}
      <View
        className="flex-row items-center rounded-lg border"
        style={{
          backgroundColor: colors.inputBg,
          borderColor: colors.inputBorder,
        }}
      >
        <TextInput
          ref={inputRef}
          value={value}
          onChangeText={(text) => {
            setValue(text.replace(/[^0-9]/g, ""));
            setError(null);
          }}
          placeholder="0"
          placeholderTextColor={colors.mutedText}
          keyboardType="number-pad"
          maxLength={3}
          className="w-12 px-2 py-1.5 text-center text-xs"
          style={{
            fontFamily: typography.families.sansMedium,
            color: colors.inputText,
          }}
          onSubmitEditing={handleAdd}
          accessibilityLabel="Custom reminder time value"
        />
      </View>

      {/* Unit selector: the shared tabs in the rounded unit shape */}
      <MoonSegmentedControl
        tabs={unitOptions.map((opt) => ({
          value: opt.id,
          label: opt.label,
          action: "reminder_unit",
        }))}
        active={unit}
        onChange={setUnit}
        shape="rounded"
        optionRole="radio"
        tone={segmentedTone}
        style={{ marginTop: 0 }}
      />

      {/* Action buttons */}
      <Pressable
        onPress={() => {
          // Check-icon only; its accessibility label is the copy.
          capturePress(pressLabel("Add custom reminder"), "add_custom_timing");
          handleAdd();
        }}
        className="h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: accentColor }}
        accessibilityRole="button"
        accessibilityLabel="Add custom reminder"
      >
        <Check size={14} color="#020617" strokeWidth={2.5} />
      </Pressable>

      <Pressable
        onPress={() => {
          // X-icon only; its accessibility label is the copy.
          capturePress(pressLabel("Cancel"), "cancel_custom_timing");
          onCancel();
        }}
        className="h-7 w-7 items-center justify-center rounded-full"
        style={{ backgroundColor: colors.cancelBg }}
        accessibilityRole="button"
        accessibilityLabel="Cancel"
      >
        <X size={14} color={colors.cancelIcon} strokeWidth={2} />
      </Pressable>

      {/* Error message */}
      {error && (
        <Text className="text-[10px]" style={{ color: colors.errorText }}>
          {error}
        </Text>
      )}
    </View>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function ReminderTimingEditor({
  timings,
  onTimingsChange,
  allowMultiple = true,
  allowCustomTiming = true,
  allowEmptySelection = false,
  disabled = false,
  accentColor = "#4ADE80",
  theme = "dark",
}: ReminderTimingEditorProps) {
  const [isAddingCustom, setIsAddingCustom] = useState(false);
  const colors: ReminderTimingTheme =
    typeof theme === "string" ? TIMING_THEMES[theme] : theme;
  const chipTone = theme === "light" ? "default" : "glass";
  const segmentedTone = theme === "light" ? "default" : "inverse";

  // Handle selecting a preset timing
  const handleSelectPreset = useCallback(
    (timing: PresetTiming) => {
      if (disabled) return;

      if (allowMultiple) {
        // Toggle selection
        if (timings.includes(timing)) {
          if (!allowEmptySelection && timings.length === 1) return;
          onTimingsChange(timings.filter((t) => t !== timing));
        } else {
          onTimingsChange([...timings, timing]);
        }
      } else {
        // Single selection mode
        onTimingsChange([timing]);
      }
    },
    [disabled, allowMultiple, allowEmptySelection, timings, onTimingsChange],
  );

  // Handle adding a custom timing
  const handleAddCustom = useCallback(
    (timing: CustomTiming) => {
      if (allowMultiple) {
        onTimingsChange([...timings, timing]);
      } else {
        onTimingsChange([timing]);
      }
      setIsAddingCustom(false);
    },
    [allowMultiple, timings, onTimingsChange],
  );

  // Handle removing a custom timing
  const handleRemoveCustom = useCallback(
    (timing: CustomTiming) => {
      if (disabled) return;
      if (!allowEmptySelection && timings.length === 1) return;
      onTimingsChange(timings.filter((t) => t !== timing));
    },
    [disabled, allowEmptySelection, timings, onTimingsChange],
  );

  // Get custom timings from selection
  const customTimings = timings.filter(isCustomTiming);

  return (
    <View className="gap-2">
      {/* Preset filter chips and optional custom-time control */}
      <View
        className={`flex-row flex-wrap gap-2 ${disabled ? "opacity-60" : ""}`}
        // @ts-expect-error - cursor is web-only style
        style={
          disabled && Platform.OS === "web"
            ? { cursor: "not-allowed" }
            : undefined
        }
        accessibilityRole="radiogroup"
        accessibilityLabel="Reminder timing options"
      >
        {/* Preset options */}
        {PRESET_TIMING_OPTIONS.map((option) => (
          <MoonFilterChip
            key={option.id}
            label={option.label}
            selected={timings.includes(option.id)}
            onPress={() => handleSelectPreset(option.id)}
            tone={chipTone}
            disabled={disabled}
          />
        ))}

        {/* Custom timings */}
        {customTimings.map((timing) => {
          const removeLabel = `Remove ${formatTimingLabel(timing)}`;
          return (
            <MoonFilterChip
              key={timing}
              label={formatTimingLabel(timing)}
              accessibilityLabel={removeLabel}
              selected
              onPress={() => handleRemoveCustom(timing)}
              onRemove={
                timings.length > 1
                  ? () => handleRemoveCustom(timing)
                  : undefined
              }
              removeAccessibilityLabel={removeLabel}
              action="remove_custom_timing"
              // The same chip as the presets beside it: a custom timing is
              // only a timing the presets do not list.
              tone={chipTone}
              showCheck={false}
              disabled={disabled}
            />
          );
        })}

        {/* Add Custom button */}
        {allowCustomTiming && !isAddingCustom && !disabled && (
          <TextActionButton
            label="Custom"
            accessibilityLabel="Add custom reminder time"
            action="open_custom_timing_form"
            onPress={() => setIsAddingCustom(true)}
            icon={Plus}
            tone={theme === "light" ? "secondary" : "onDark"}
            size="sm"
          />
        )}
      </View>

      {/* Custom time input */}
      {allowCustomTiming && isAddingCustom && (
        <CustomTimeInput
          onAdd={handleAddCustom}
          onCancel={() => setIsAddingCustom(false)}
          existingTimings={timings}
          accentColor={accentColor}
          colors={colors}
          segmentedTone={segmentedTone}
        />
      )}
    </View>
  );
}
